import { webApiService } from '@/services'
import { IConfigSeller } from '@/models/configSeller'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { MdCheckCircle, MdHourglassBottom, MdRequestQuote, MdSavings } from 'react-icons/md'
import { currencyFormat } from '@/utils/currencyFormat'
import { EXCLUDED_FROM_RETOMAS_STATUS_IDS, STATUS_GROUPS, TradeinKpiKey } from '@/utils/tradeinStatusGroups'
import { computeTrend, IStatusTrendRow } from '@/utils/tradeinTrends'
import StatusCard from './StatusCard'
import RetomasBreakdownModal from './RetomasBreakdownModal'

interface ListCardProps {
  selectedKpi: TradeinKpiKey
  onSelectKpi: (kpi: TradeinKpiKey) => void
}

export interface IStatusIndicador {
  id_status: number
  status_name: string
  cantidad: number | string
  monto: number | string
}

/* Acentos por widget calcados de la referencia visual del usuario (verde/azul/morado/ámbar):
   no son colores oficiales del brandbook (REUSE_COLORS), se eligieron solo para igualar esa
   imagen puntual. La card "Finalizados" activa sigue con el gradiente oscuro existente. */
export const KPI_ACCENTS = {
  cotizados: '#22c55e',
  evaluacion: '#3b82f6',
  finalizados: '#37417f',
  retomas: '#eab308'
}

const ListCard = ({ selectedKpi, onSelectKpi }: ListCardProps) => {
  const [data, setData] = useState<{ loading: boolean, listIndicadores: IStatusIndicador[] }>({ loading: false, listIndicadores: [] })
  const [trends, setTrends] = useState<{ loading: boolean, rows: IStatusTrendRow[] }>({ loading: false, rows: [] })
  const [showBreakdown, setShowBreakdown] = useState(false)
  const userState = useSelector((store: any) => store.user)
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const isWom = configSeller.config.s3.FOLDER_APP === 'ti_wom'
  const { slug } = useParams<{ slug: string }>()

  const loadData = (): void => {
    setData((prev) => ({ ...prev, loading: true }))
    const womParams = isWom ? { sso: userState.sso === true, id_role: Number(userState.id_role) } : undefined
    webApiService.getCountStatus(userState.email, String(slug), womParams).then((res) => {
      setData({ listIndicadores: res.data ?? [], loading: false })
    })
      .catch((error) => {
        console.log('Error loading data:', error)
        setData((prev) => ({ ...prev, loading: false }))
      })
  }

  const loadTrends = (): void => {
    setTrends((prev) => ({ ...prev, loading: true }))
    webApiService.getStatusTrends(String(slug)).then((res) => {
      setTrends({ rows: res.data ?? [], loading: false })
    })
      .catch((error) => {
        console.log('Error loading trends:', error)
        setTrends((prev) => ({ ...prev, loading: false }))
      })
  }

  useEffect(() => {
    loadData()
    loadTrends()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sumBy = (ids: number[], field: 'cantidad' | 'monto'): number => data.listIndicadores
    .filter((row) => ids.includes(row.id_status))
    .reduce((acc, row) => acc + Number(row[field] ?? 0), 0)

  /* monto vigente: todo lo que no haya quedado en un estado final negativo (No Aprobado, Rechazado)
     ni en un estado aún no clasificado (ej. Caducado). */
  const retomasIds = data.listIndicadores
    .map((row) => row.id_status)
    .filter((id) => !EXCLUDED_FROM_RETOMAS_STATUS_IDS.includes(id))
  const montoRetomas = sumBy(retomasIds, 'monto')

  /* tendencia semanal real (% vs. semana anterior + sparkline de 7 días), calculada a partir de
     getStatusTrends. Si la semana anterior no tiene datos reales con qué comparar, pctChange
     viene null y StatusCard omite el badge en vez de fabricar un número. */
  const cotizadosTrend = computeTrend(trends.rows, (id) => STATUS_GROUPS.cotizados.includes(id), 'cantidad')
  const evaluacionTrend = computeTrend(trends.rows, (id) => STATUS_GROUPS.evaluacion.includes(id), 'cantidad')
  const finalizadosTrend = computeTrend(trends.rows, (id) => STATUS_GROUPS.finalizados.includes(id), 'cantidad')
  const retomasTrend = computeTrend(trends.rows, (id) => !EXCLUDED_FROM_RETOMAS_STATUS_IDS.includes(id), 'monto')

  return (
    <>
      <div className='grid gap-4 grid-cols-2 md:grid-cols-4'>
        <StatusCard
          active={selectedKpi === 'cotizados'}
          onPress={() => onSelectKpi('cotizados')}
          loading={data.loading}
          indicador={sumBy(STATUS_GROUPS.cotizados, 'cantidad')}
          estado='Cotizados hoy'
          Icon={<MdRequestQuote size={40} />}
          accentColor={KPI_ACCENTS.cotizados}
          trendPct={cotizadosTrend.pctChange}
          sparkline={cotizadosTrend.sparkline}
        />

        <StatusCard
          active={selectedKpi === 'evaluacion'}
          onPress={() => onSelectKpi('evaluacion')}
          loading={data.loading}
          indicador={sumBy(STATUS_GROUPS.evaluacion, 'cantidad')}
          estado='En evaluación'
          Icon={<MdHourglassBottom size={40} />}
          accentColor={KPI_ACCENTS.evaluacion}
          trendPct={evaluacionTrend.pctChange}
          sparkline={evaluacionTrend.sparkline}
        />

        <StatusCard
          active={selectedKpi === 'finalizados'}
          onPress={() => onSelectKpi('finalizados')}
          loading={data.loading}
          indicador={sumBy(STATUS_GROUPS.finalizados, 'cantidad')}
          estado='Finalizados'
          Icon={<MdCheckCircle size={40} />}
          accentColor={KPI_ACCENTS.finalizados}
          trendPct={finalizadosTrend.pctChange}
          sparkline={finalizadosTrend.sparkline}
        />

        <StatusCard
          active={false}
          onPress={() => setShowBreakdown(true)}
          loading={data.loading}
          indicador={montoRetomas}
          formatValue={(value) => currencyFormat(value, import.meta.env.VITE_COUNTRY)}
          estado='Total en retomas'
          Icon={<MdSavings size={40} />}
          accentColor={KPI_ACCENTS.retomas}
          trendPct={retomasTrend.pctChange}
          sparkline={retomasTrend.sparkline}
        />
      </div>

      <RetomasBreakdownModal
        isOpen={showBreakdown}
        onClose={() => setShowBreakdown(false)}
        listIndicadores={data.listIndicadores}
        loading={data.loading}
        montoRetomas={montoRetomas}
        trendPct={retomasTrend.pctChange}
        sparkline={retomasTrend.sparkline}
      />
    </>
  )
}

export default ListCard
